import axios from 'axios';
import { getAuthHeader } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const apiClient = axios.create();

apiClient.interceptors.request.use((config) => {
  const authHeaders = getAuthHeader();
  config.headers = { ...config.headers, ...authHeaders };
  return config;
});

// Properties API
export const getProperties = async (status = null) => {
  const params = status ? { status } : {};
  const response = await apiClient.get(`${API}/properties`, { params });
  return response.data;
};

export const getProperty = async (id) => {
  const response = await apiClient.get(`${API}/properties/${id}`);
  return response.data;
};

export const getPropertyBySlug = async (slug) => {
  const response = await apiClient.get(`${API}/properties/by-slug/${slug}`);
  return response.data;
};

export const createProperty = async (propertyData) => {
  const response = await apiClient.post(`${API}/properties`, propertyData);
  return response.data;
};

export const updateProperty = async (id, propertyData) => {
  const response = await apiClient.put(`${API}/properties/${id}`, propertyData);
  return response.data;
};

export const deleteProperty = async (id) => {
  const response = await apiClient.delete(`${API}/properties/${id}`);
  return response.data;
};

// Content API
export const getCompanyInfo = async () => {
  const response = await apiClient.get(`${API}/content/company-info`);
  return response.data;
};

export const updateCompanyInfo = async (companyData) => {
  const response = await apiClient.put(`${API}/content/company-info`, companyData);
  return response.data;
};

export const getQualifications = async () => {
  const response = await apiClient.get(`${API}/content/qualifications`);
  return response.data;
};

export const updateQualifications = async (qualificationsData) => {
  const response = await apiClient.put(`${API}/content/qualifications`, qualificationsData);
  return response.data;
};

export const getFAQs = async () => {
  const response = await apiClient.get(`${API}/content/faqs`);
  return response.data;
};

export const createFAQ = async (faqData) => {
  const response = await apiClient.post(`${API}/content/faqs`, faqData);
  return response.data;
};

export const updateFAQ = async (id, faqData) => {
  const response = await apiClient.put(`${API}/content/faqs/${id}`, faqData);
  return response.data;
};

export const deleteFAQ = async (id) => {
  const response = await apiClient.delete(`${API}/content/faqs/${id}`);
  return response.data;
};

export const updateFAQs = async (faqsData) => {
  const response = await apiClient.put(`${API}/content/faqs`, faqsData);
  return response.data;
};

// Media API
export const uploadMedia = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`${API}/media/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const bulkUploadMedia = async (files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  const response = await apiClient.post(`${API}/media/bulk-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getMediaFiles = async () => {
  const response = await apiClient.get(`${API}/media`);
  return response.data;
};

export const deleteMedia = async (id) => {
  const response = await apiClient.delete(`${API}/media/${id}`);
  return response.data;
};

// Site Settings API
export const getSiteSettings = async () => {
  const response = await apiClient.get(`${API}/settings`);
  return response.data;
};

export const updateSiteSettings = async (settingsData) => {
  const response = await apiClient.put(`${API}/settings`, settingsData);
  return response.data;
};

// Team Members API
export const getTeamMembers = async (enabledOnly = false) => {
  const params = enabledOnly ? { enabled_only: true } : {};
  const response = await apiClient.get(`${API}/team-members`, { params });
  return response.data;
};

export const getTeamMember = async (id) => {
  const response = await apiClient.get(`${API}/team-members/${id}`);
  return response.data;
};

export const createTeamMember = async (memberData) => {
  const response = await apiClient.post(`${API}/team-members`, memberData);
  return response.data;
};

export const updateTeamMember = async (id, memberData) => {
  const response = await apiClient.put(`${API}/team-members/${id}`, memberData);
  return response.data;
};

export const deleteTeamMember = async (id) => {
  const response = await apiClient.delete(`${API}/team-members/${id}`);
  return response.data;
};

// Availability API
export const getAvailability = async () => {
  const response = await apiClient.get(`${API}/availability`);
  return response.data;
};

export const updateAvailability = async (availabilityData) => {
  const response = await apiClient.put(`${API}/availability`, availabilityData);
  return response.data;
};

// Appointments API
export const getAppointments = async () => {
  const response = await apiClient.get(`${API}/appointments`);
  return response.data;
};

export const createAppointment = async (appointmentData) => {
  const response = await apiClient.post(`${API}/appointments`, appointmentData);
  return response.data;
};

export const updateAppointment = async (id, updateData) => {
  const response = await apiClient.put(`${API}/appointments/${id}`, updateData);
  return response.data;
};

export const deleteAppointment = async (id) => {
  const response = await apiClient.delete(`${API}/appointments/${id}`);
  return response.data;
};

// Contact Page API
export const getContactPage = async () => {
  const response = await apiClient.get(`${API}/contact-page`);
  return response.data;
};

export const updateContactPage = async (contactData) => {
  const response = await apiClient.put(`${API}/contact-page`, contactData);
  return response.data;
};


// SMTP Settings API
export const getSMTPSettings = async () => {
  const response = await apiClient.get(`${API}/smtp-settings`);
  return response.data;
};

export const updateSMTPSettings = async (settingsData) => {
  const response = await apiClient.put(`${API}/smtp-settings`, settingsData);
  return response.data;
};

